/**
 * Maps every application capability to the name of the policy registered in the
 * policy registry (the same ones the backend evaluates for the corresponding
 * operations).
 *
 * Several capabilities may point to the same policy: the keys describe the spot
 * in the interface, the values the authorization. Renaming or merging a policy
 * happens only here, the components are left untouched.
 *
 * EMPTY on purpose: for this app the registry does not expose any policy yet. Do
 * not invent plausible ones. To add one, it's a single line:
 *
 *     taxSubmissionSend: "<exact.policy.name>",
 *
 * and from there `usePermission("taxSubmissionSend")` and
 * `<Can policy="taxSubmissionSend">` become usable. As long as the map is empty
 * `PolicyKey` is `never`, so calling them is a compile error — that is intended.
 */
export const POLICIES = {} as const satisfies Record<string, string>;

/** Application capability: how the components call it. */
export type PolicyKey = keyof typeof POLICIES;

/** Policy name in the registry: what actually gets evaluated. */
export type PolicyName = (typeof POLICIES)[PolicyKey];

/** The policies to evaluate: the DISTINCT names, not one per capability. */
export const POLICY_NAMES: readonly PolicyName[] = Array.from(
    new Set<PolicyName>(Object.values(POLICIES)),
);
